import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { CASINO_GAMES_LOAD_MORE } from '@/config/casino'
import { useGetCasinoGamesQuery } from '@/store/api/casinoApi'
import type { CasinoGame, CasinoGamesRequest } from '@/types/casino'

export function useInfiniteCasinoGames(request: Omit<CasinoGamesRequest, 'page' | 'per_page'>) {
	const [page, setPage] = useState(1)
	const [games, setGames] = useState<CasinoGame[]>([])
	const [total, setTotal] = useState(0)
	const requestKey = JSON.stringify(request)

	useEffect(() => {
		setPage(1)
		setGames([])
		setTotal(0)
	}, [requestKey])

	const { data, isLoading, isError, isFetching } = useGetCasinoGamesQuery({
		...request,
		page,
		per_page: CASINO_GAMES_LOAD_MORE,
	})

	useEffect(() => {
		if (!data) {
			return
		}

		setTotal(data.total)

		if (page === 1) {
			setGames(data.data)
			return
		}

		setGames((previousGames) => {
			const existingIds = new Set(_.map(previousGames, 'id'))
			const appendedGames = _.filter(data.data, (game) => !existingIds.has(game.id))

			return [...previousGames, ...appendedGames]
		})
	}, [data, page])

	const hasMore = games.length < total

	const loadMore = useCallback(() => {
		if (!hasMore || isFetching) {
			return
		}

		setPage((currentPage) => currentPage + 1)
	}, [hasMore, isFetching])

	return {
		games,
		isLoading: isLoading && page === 1,
		isLoadingMore: isFetching && page > 1,
		isError,
		hasMore,
		loadMore,
	}
}
